import { getWorkflowConfigurationTriggerIndexFromTriggerKey } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphCanvas/useWorkflowGraph.helpers";
import { useWorkflowGraphInteractions } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphCanvas/useWorkflowGraphInteractions";
import { useWorkflowGraphState } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphCanvas/WorkflowGraphStore";
import {
  getWorkflowTaskIconFromType,
  getWorkflowTaskNameFromType,
  getWorkflowTriggerDescriptionFromTrigger,
  getWorkflowTriggerIconFromTrigger,
  getWorkflowTriggerNameFromTrigger,
} from "#src/components/Workflows/WorkflowsHelpers";
import {
  Accordion,
  Drawer,
  DrawerProps,
  Radio,
  TextDataDisplay,
  TextInputBare,
  useSearch,
} from "@validereinc/common-components";
import {
  SystemActionType,
  UserTaskType,
  WorkflowStepType,
  WorkflowTriggerEvent,
  WorkflowTriggerEventType,
  WorkflowTriggerType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { WorkflowConfigurationAddNodeDrawerProps } from "./types";
import { getNewStepConfigFromSelectedOption } from "./WorkflowConfigurationAddNodeDrawer.helpers";
import styles from "./WorkflowConfigurationAddNodeDrawer.module.scss";
import { WorkflowStepListItem } from "./WorkflowStepListItem";

const cx = classNames.bind(styles);

const USER_TASKS = Object.values(UserTaskType).map((userTask) => ({
  label: getWorkflowTaskNameFromType(userTask),
  icon: getWorkflowTaskIconFromType(userTask),
  value: userTask,
  type: WorkflowStepType.USER_TASK,
  taskType: userTask,
}));

const SYSTEM_ACTIONS = Object.values(SystemActionType)
  .map((systemAction) => ({
    label: getWorkflowTaskNameFromType(systemAction),
    icon: getWorkflowTaskIconFromType(systemAction),
    value: systemAction,
    type: WorkflowStepType.SYSTEM_ACTION,
    taskType: systemAction,
  }))
  // UPDATE_CUSTOM_ATTRIBUTES is deprecated and replaced with UPDATE_ATTRIBUTES
  .filter(
    (systemAction) =>
      systemAction.value !== SystemActionType.UPDATE_CUSTOM_ATTRIBUTES
  );

const PARALLEL_STEP = [
  {
    label: getWorkflowTaskNameFromType(WorkflowStepType.PARALLEL),
    icon: getWorkflowTaskIconFromType(WorkflowStepType.PARALLEL),
    value: WorkflowStepType.PARALLEL,
    type: WorkflowStepType.PARALLEL,
    taskType: WorkflowStepType.PARALLEL,
  },
];

const STEPS = [...USER_TASKS, ...SYSTEM_ACTIONS, ...PARALLEL_STEP];

const TRIGGER_OPTIONS = [
  {
    type: WorkflowTriggerType.SCHEDULED,
  },
  {
    type: WorkflowTriggerType.SYSTEM,
    event: WorkflowTriggerEvent.FORM_SUBMISSION_CREATE,
  },
  {
    type: WorkflowTriggerType.SYSTEM,
    event: WorkflowTriggerEvent.ASSET_CREATE,
  },
  {
    type: WorkflowTriggerType.SYSTEM,
    event: WorkflowTriggerEvent.ASSET_UPDATE,
  },
  {
    type: WorkflowTriggerType.SYSTEM,
    event: WorkflowTriggerEvent.WORKFLOW_COMPLETE,
  },
];

const TRIGGERS = Object.values(TRIGGER_OPTIONS).map((trigger) => ({
  title: getWorkflowTriggerNameFromTrigger(
    {
      type: trigger.type,
      event: trigger.event,
    },
    true
  ),
  icon: getWorkflowTriggerIconFromTrigger({
    type: trigger.type,
    event: trigger.event,
  }),
  value: trigger.event?.toString() ?? trigger.type.toString(),
  description: getWorkflowTriggerDescriptionFromTrigger({
    type: trigger.type,
    event: trigger.event,
  }),
  type: "trigger",
}));

const WorkflowConfigurationAddNodeDrawer = ({
  isOpen,
  onClose,
  selectedNode,
}: WorkflowConfigurationAddNodeDrawerProps) => {
  const nodeType =
    selectedNode?.data.variant === "trigger" ? "trigger" : "step";

  const [searchTerm, setSearchTerm] = useState("");

  const { data: filteredSteps } = useSearch(STEPS, (step) =>
    step.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const { data: filteredTriggers } = useSearch(TRIGGERS, (trigger) =>
    trigger.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const {
    isNodeDirty,
    revertTaskChanges,
    originalTriggers,
    selectedNodeId = "",
  } = useWorkflowGraphState();

  const handleClose = useCallback(() => {
    if (selectedNodeId && isNodeDirty(selectedNodeId)) {
      const isTrigger = selectedNodeId.startsWith("trigger-");
      if (isTrigger) {
        const triggerIndex =
          getWorkflowConfigurationTriggerIndexFromTriggerKey(selectedNodeId);
        if (!originalTriggers || triggerIndex >= originalTriggers.length) {
          revertTaskChanges(selectedNodeId);
        }
      }
    }
    onClose();
    setSearchTerm("");
  }, [
    selectedNodeId,
    isNodeDirty,
    revertTaskChanges,
    onClose,
    setSearchTerm,
    originalTriggers,
  ]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleClose]);

  const { onAddTrigger } = useWorkflowGraphInteractions();

  const drawerProps: Omit<
    DrawerProps,
    "isOpen" | "onClose" | "children" | "actionRow"
  > = useMemo(() => {
    return {
      title: nodeType === "step" ? "Add New Step" : "Add New Trigger",
    };
  }, [nodeType]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      mode="non-blocking"
      {...drawerProps}
    >
      {nodeType === "trigger" && (
        <TextDataDisplay
          value="Trigger Type"
          className={cx("drawerContentLabel")}
        />
      )}
      <TextInputBare
        type="search"
        name="search"
        placeholder={`Search ${nodeType === "step" ? "Steps" : "Trigger Types"}...`}
        value={searchTerm}
        onChange={(val) => setSearchTerm(val)}
        style={{ marginBottom: 16 }}
      />
      <div className={cx("contentContainer")}>
        {nodeType === "step" ? (
          <Accordion
            defaultActiveKeys={["user-tasks", "automations", "parallel-step"]}
          >
            <Accordion.AccordionPanel
              title="Tasks"
              dataKey="user-tasks"
            >
              {filteredSteps
                .filter((step) => step.type === WorkflowStepType.USER_TASK)
                .map((userTask) => (
                  <WorkflowStepListItem
                    key={userTask.value}
                    stepDetails={userTask}
                    getNewStepConfigFromSelectedOption={
                      getNewStepConfigFromSelectedOption
                    }
                    onClose={handleClose}
                  />
                ))}
            </Accordion.AccordionPanel>
            <Accordion.AccordionPanel
              title="Automations"
              dataKey="automations"
            >
              {filteredSteps
                .filter((step) => step.type === WorkflowStepType.SYSTEM_ACTION)
                .map((systemAction) => (
                  <WorkflowStepListItem
                    key={systemAction.value}
                    stepDetails={systemAction}
                    getNewStepConfigFromSelectedOption={
                      getNewStepConfigFromSelectedOption
                    }
                    onClose={handleClose}
                  />
                ))}
            </Accordion.AccordionPanel>
            <Accordion.AccordionPanel
              title="Parallel Step"
              dataKey="parallel-step"
            >
              {filteredSteps
                .filter((step) => step.type === WorkflowStepType.PARALLEL)
                .map((parallelStep) => (
                  <WorkflowStepListItem
                    key={parallelStep.value}
                    stepDetails={parallelStep}
                    getNewStepConfigFromSelectedOption={
                      getNewStepConfigFromSelectedOption
                    }
                    onClose={handleClose}
                  />
                ))}
            </Accordion.AccordionPanel>
          </Accordion>
        ) : (
          <>
            <Radio
              className={cx("workflowTriggerTypesCardContainer")}
              optionClassName={cx("workflowTriggerTypeCard")}
              name="trigger-options"
              value={""}
              onChange={(selectedOption) => {
                handleClose();
                onAddTrigger(
                  selectedOption as WorkflowTriggerEventType & "scheduled"
                );
              }}
              options={filteredTriggers}
              isLoading={false}
              isDisabled={false}
              shouldUseRadioSelectionCard
              shouldShowRadioIcon={false}
            />
          </>
        )}
      </div>
    </Drawer>
  );
};

export default WorkflowConfigurationAddNodeDrawer;
